import { Injectable, signal } from '@angular/core';
import { BackgroundVariant, VariantType } from '../models/hero/hero-variant.enum';

/**
 * Service that manages the breadcrumb background variant.
 * The variant is set by hero components and consumed by the breadcrumb component.
 *
 * The first hero component to initialize sets the variant for the breadcrumb.
 * If no hero sets a variant, it defaults to LIGHT (dark text on light background).
 */
@Injectable({
    providedIn: 'root',
})
export class BreadcrumbService {
    /**
     * Signal that holds the current breadcrumb background variant.
     * Defaults to LIGHT (dark text on light background).
     */
    private readonly _variant = signal<BackgroundVariant>(BackgroundVariant.LIGHT);

    /**
     * Read-only signal for the breadcrumb variant.
     * Use this in components for reactive updates.
     */
    readonly variant = this._variant.asReadonly();

    /**
     * Gets the current breadcrumb variant.
     *
     * @deprecated This getter is for backward compatibility with consuming applications.
     * Use the `variant` signal directly instead: `breadcrumbService.variant()`.
     * This will be removed in the next major version.
     */
    get color(): BackgroundVariant {
        return this._variant();
    }

    /**
     * Sets the breadcrumb variant.
     * Typically called by hero components during initialization.
     *
     * @param value - The variant to set.
     * @deprecated Use `setVariant()` instead. VariantType support will be removed in next major version.
     * TODO: Remove VariantType support in next major version - only accept BackgroundVariant
     */
    set color(value: BackgroundVariant | VariantType) {
        this.setVariant(value as BackgroundVariant);
    }

    /**
     * Sets the breadcrumb variant.
     *
     * @param value - The variant to set.
     */
    setVariant(value: BackgroundVariant): void {
        this._variant.set(value);
    }

    /**
     * Resets the variant to the default (LIGHT).
     * Called when a hero component is destroyed.
     */
    reset(): void {
        this._variant.set(BackgroundVariant.LIGHT);
    }
}
